import React, { useState, useRef, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import "./CalendarMain.css";
import { Container } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import ModalFormTaskSingolo from "./ModalFormTaskSingolo.tsx";
import { RootState } from "../Redux/Store/index.ts";
import { Task } from "../lib/interface.ts";

interface CalendarEvent {
  title: string;
  start: string;
  id: string;
  type: "evento" | "task";
  cantiereId?: string;
  eventoId?: string;
}

const CalendarMain: React.FC = () => {
  const [currentView, setCurrentView] = useState("dayGridMonth");
  const calendarRef = useRef<FullCalendar | null>(null);
  const navigate = useNavigate();

  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const { eventi } = useSelector((state: RootState) => state.eventi);
  const { task } = useSelector((state: RootState) => state.task);
  const [iscreate, setIsCreate] = useState(false);
  const [type, setType] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [taskModal, setTaskModal] = useState<Task>({
    id: "",
    titolo: "",
    descrizione: "",
    dataInizio: "",
    scadenza: "",
    dataFineEffettiva: "",
    priorita: "",
    promemoria: "",
    dataPromemoriaPersonalizzata: "",
    cantiereId: "",
    contrattoQuadroId: "",
    eventoId: "",
    assegnatoA: "",
    stato: "",
  });

  const calendarEvents: CalendarEvent[] = [
    ...eventi.map((evento) => ({
      title: evento.titolo,
      start: evento.scadenza ? evento.scadenza.split("T")[0] : evento.scadenza,
      id: evento.id,
      type: "evento",
    })),
    ...task.map((taskItem) => ({
      title: taskItem.titolo,
      start: taskItem.scadenza ? taskItem.scadenza.split("T")[0] : taskItem.scadenza,
      id: taskItem.id,
      type: "task",
      cantiereId: taskItem.cantiereId || undefined,
      eventoId: taskItem.eventoId || undefined,
    })),
  ];

  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(currentView);
    }
  }, [currentView]);

  const handleDateClick = (info: any) => {
    setDropdownPosition({ x: info.jsEvent.clientX, y: info.jsEvent.clientY }); // Posiziona il menu
    setShowDropdown(true); // Mostra il menu a discesa
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowDropdown(false); // Chiude il menu a discesa
    }
  };

  useEffect(() => {
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    // Pulizia dell'event listener quando il componente si smonta o il menu si chiude
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  const handleOptionClick = (option: string) => {
    setShowDropdown(false);
    setIsCreate(true);
    setModalOpen(true);
    setTaskModal({
      id: "",
      titolo: "",
      descrizione: "",
      dataInizio: "",
      scadenza: "",
      dataFineEffettiva: "",
      priorita: "",
      promemoria: "",
      dataPromemoriaPersonalizzata: "",
      cantiereId: "",
      contrattoQuadroId: "",
      eventoId: "",
      assegnatoA: "",
      stato: "",
    });

    setType(option);
  };

  const handleEventClick = (clickInfo: any) => {
    const clickedEvent: CalendarEvent = calendarEvents.find(
      (event) => event.id === clickInfo.event.id
    ) as CalendarEvent;

    if (clickedEvent.type === "task") {
      const singleTask = task.find((t) => t.id === clickedEvent.id);
      setIsCreate(false);
      setModalOpen(true);
      if (clickedEvent.cantiereId) {
        setType("cantiere");
      } else if (clickedEvent.eventoId) {
        setType("evento");
      } else {
        setType("singolo");
      }
      setTaskModal(singleTask);
    }

    if (clickedEvent.type === "evento") {
      navigate(`/modifica/eventi/${clickedEvent.id}`);
    } else {
      console.warn("Tipo di evento sconosciuto.");
    }
  };

  return (
    <>
      <div id="calendar-main-container" className={currentView === "timeGridWeek" ? "week-view" : ""}>
        <div className="calendar-header">
          <h2 className="titolo-calendario">Calendario</h2>
          <div className="view-buttons-wrapper">
            <button
              onClick={() => setCurrentView("timeGridDay")}
              className={currentView === "timeGridDay" ? "active" : ""}
            >
              Giorno
            </button>

            <button
              onClick={() => setCurrentView("timeGridWeek")}
              className={currentView === "timeGridWeek" ? "active" : ""}
            >
              Sett
            </button>
            <button
              onClick={() => setCurrentView("dayGridMonth")}
              className={currentView === "dayGridMonth" ? "active" : ""}
            >
              Mese
            </button>
          </div>
        </div>
        <Container fluid>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            events={calendarEvents}
            locale="it"
            fixedWeekCount={false}
            firstDay={1}
            showNonCurrentDates={true}
            slotMinTime="24:00:00"
            slotMaxTime="24:00:00"
            buttonText={{
              today: "Oggi",
            }}
            height="auto"
            eventContent={(eventInfo) => <div className="text-center">{eventInfo.event.title}</div>}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
          />
        </Container>

        {showDropdown && (
          <div
            ref={dropdownRef}
            className="dropdown-menu show"
            style={{
              position: "absolute",
              top: dropdownPosition.y,
              left: dropdownPosition.x,
              zIndex: 1000,
            }}
          >
            <button className="dropdown-item" onClick={() => handleOptionClick("cantiere")}>
              Crea task da cantiere
            </button>
            <button className="dropdown-item" onClick={() => handleOptionClick("evento")}>
              Crea task da evento
            </button>
            <button className="dropdown-item" onClick={() => handleOptionClick("singolo")}>
              Crea singolo task
            </button>
          </div>
        )}
      </div>
      <ModalFormTaskSingolo
        taskModal={taskModal}
        isModalOpen={isModalOpen}
        setModalOpen={setModalOpen}
        type={type}
        iscreate={iscreate}
      />
    </>
  );
};

export default CalendarMain;
